import { useMutation, useQuery } from 'react-query';
import { GQLGetRoles } from '../services/querys';
import { useState } from 'react';
import { Roles } from '../types';
import { getListing } from 'graphql/listings/queries';
import { LISTING_CODE_EXISTING_MUTATIONS, LISTING_CODE_EXISTING_QUERIES } from 'utils/constants';
import { GQLCreateRole, GQLDeleteRole, GQLUpdateRole } from '../services/mutations';
import { capitalize } from 'utils/helpers';
import useAlert from 'utils/alert';
import { useTranslation } from 'react-i18next';
import { normalizeLedgerError } from 'components/ledger-entry-editor/engine/errors';

export const getRoleDeletionErrorMessage = (error: any) => {
  const graphqlMessages = error?.response?.errors
    ?.map((graphqlError: any) => graphqlError?.message)
    .filter(Boolean);

  if (graphqlMessages?.length) {
    return Array.from(new Set(graphqlMessages)).join('; ');
  }

  return normalizeLedgerError(error);
};

const useRoles = () => {
  const [roles, setRoles] = useState<Roles[]>([]);
  const [selectedRole, setSelectedRole] = useState<Roles>();

  const showAlert = useAlert();
  const { t } = useTranslation();

  useQuery(['order-roles'], GQLGetRoles, {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data) {
        const { getRoles } = data;
        setRoles(getRoles.sort((a, b) => a.description.localeCompare(b.description)));
      }
    },
  });

  const { data: listingQueriesResponse } = useQuery(['getListing', LISTING_CODE_EXISTING_QUERIES], {
    queryFn: () => getListing({ code: LISTING_CODE_EXISTING_QUERIES }),
    refetchOnWindowFocus: false,
  });

  const listingQueries =
    listingQueriesResponse?.getListing.entries.sort((a, b) =>
      a.description.localeCompare(b.description)
    ) || [];

  const { data: listingMutationsResponse } = useQuery(
    ['getListing', LISTING_CODE_EXISTING_MUTATIONS],
    {
      queryFn: () => getListing({ code: LISTING_CODE_EXISTING_MUTATIONS }),
      refetchOnWindowFocus: false,
    }
  );

  const listingMutations =
    listingMutationsResponse?.getListing.entries.sort((a, b) =>
      a.description.localeCompare(b.description)
    ) || [];

  const { mutate: createRoleFunc, ...responseCreateRole } = useMutation(GQLCreateRole, {
    onSuccess: (data) => {
      showAlert(capitalize(t('dialogs:order-updated')), 'success');
      const { createRole } = data;
      setRoles([...roles, createRole].sort((a, b) => a.description.localeCompare(b.description)));
      responseCreateRole.reset();
    },
  });

  const handleCreateRole = (data) => {
    createRoleFunc(data);
  };

  const { mutate: deleteRoleFunc, ...responseDeleteRole } = useMutation(GQLDeleteRole, {
    onSuccess: (data) => {
      showAlert(capitalize(t('dialogs:order-updated')), 'success');
      const { deleteRole } = data;
      setRoles(roles.filter((role) => role._id !== deleteRole._id));
      responseDeleteRole.reset();
      if (deleteRole._id === selectedRole?._id) {
        setSelectedRole(undefined);
      }
    },
    onError: (error: any) => {
      showAlert(getRoleDeletionErrorMessage(error), 'error');
    },
  });

  const handleDeleteRole = (data) => {
    deleteRoleFunc(data);
  };

  const { mutate: updateRoleFunc, ...responseUpdateRole } = useMutation(GQLUpdateRole, {
    onSuccess: (data) => {
      showAlert(capitalize(t('dialogs:order-updated')), 'success');
      const { updateRole } = data;
      setRoles((prev) =>
        prev
          .map((perm) => (perm._id === updateRole._id ? { ...updateRole } : perm))
          .sort((a, b) => a.description.localeCompare(b.description))
      );
      responseUpdateRole.reset();
    },
  });

  const handleUpdateRole = (data) => {
    updateRoleFunc(data);
  };

  return {
    roles,
    listingQueries,
    listingMutations,
    handleCreateRole,
    handleDeleteRole,
    setSelectedRole,
    selectedRole,
    handleUpdateRole,
  };
};

export default useRoles;
